import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PDV, ReportItem } from '../../AdminClasses';
import { Button, Groupper, Input, Message, useFrontier, Modal } from 'react-frontier2';
import { bindClick, fromUnix } from '@arema/components/Util';
import { CatalogEvent, SetLoading } from '@arema/components/Classes';
import { useValidator } from '@arema/components/Hooks';
import { SearchEventModal } from '../../components';
import API from '../../API';
import moment from 'moment';

interface DownloadProps{
	date_start?: number,
	date_end?: number,
	event_id?: number,
	date_id?: number,
}

interface PdvReportsProps{
	pdv_id: number, 
	pdv: PDV,
}

enum Modals{
	ASK_DATE = 1,
	SELECT_EVENT = 2,
}

var PdvReports = (props: PdvReportsProps)=>{
	var { t } = useTranslation();
	var { Toast } = useFrontier();
	var [downloading, setDownloading] = useState<boolean>(false);
	var [dateReport, setDateReport] = useState<number>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var dates = useValidator({
		date_start: moment().subtract(1, 'month').startOf('day').unix(),
		date_end: moment().endOf('day').unix(),
	}, {
		date_start: ['empty'],
		date_end: ['empty']
	});
	
	const REPORTS : ReportItem<DownloadProps>[] = [{
		title: t('pdv_reports.sales.title'), 
		description: t('pdv_reports.sales.description'),
		icon: 'file-pdf',
		ask_date_range: true,
		onDownload: async (p: DownloadProps)=>{
			try{
				var res = await API.getPDVSalesLog(props.pdv_id, p.date_start, p.date_end);
				if(res.error){
					Toast.error(res.message);
					return null
				}
				return res.data.url;
			}catch(e){
				Toast.error(t('common.unexpected_code', { code: 'LCL-PDVRP-SLG' }));
				return null;
			}
		},
	}, {
		title: t('pdv_reports.log.title'),
		description: t('pdv_reports.log.description'),
		icon: 'file-csv',
		ask_event: true,
		ask_date_range: true,
		onDownload: async (p: DownloadProps)=>{
			var tx = Toast.show(t('common.loading'), {
				loading: true,
				dismissable: false,
			});
			try{
				var res = await API.getPdvCorteLog(props.pdv_id, p.event_id, p.date_start, p.date_end);
				if(res.error){
					Toast.error(res.message);
					return null;
				}
				return res.data?.url;
			}catch(e){
				Toast.error(t('common.unexpected_code', { code: 'LCL-PDVRP-ELG' }));
				return null;
			}finally{
				Toast.dismiss(tx);
			}
		}
	}, {
		title: t('pdv_reports.ticket_log.title'),
		description: t('pdv_reports.ticket_log.description'),
		icon: 'file-csv',
		ask_event: true,
		ask_date_range: true,
		onDownload: async (p: DownloadProps)=>{
			var tx = Toast.show(t('common.loading'), {
				loading: true,
				dismissable: false,
			});
			try{
				var res = await API.getPdvTicketLog(props.pdv_id, p.event_id, p.date_start, p.date_end);
				if(res.error){
					Toast.error(res.message);
					return null;
				}
				return res.data?.url;
			}catch(e){
				Toast.error(t('common.unexpected_code', { code: 'LCL-PDVRP-TKLG' }));
				return null;
			}finally{
				Toast.dismiss(tx);
			}
		}
	}];

	var askDate = (report_ix: number)=>{
		setShownModal(Modals.ASK_DATE);
		setDateReport(report_ix);
	}

	var askEvent = (report_ix: number)=>{
		setShownModal(Modals.SELECT_EVENT);
		setDateReport(report_ix);
	}

	var onDateSelected = (setLoading: SetLoading)=>{
		if(dateReport===null) return;
		var rp = REPORTS[dateReport];
		if(!rp) return;

		if(rp.ask_event){
			return askEvent(dateReport);
		}

		var { valid } = dates.validate();
		if(!valid) return;
		setLoading(true);
		rp.onDownload({
			date_start: dates.data.date_start,
			date_end: dates.data.date_end,
		}).then(url=>{
			setDateReport(null);
			if(!url) return;
			window.open(url, '_blank');
		}).catch(err=>{
			Toast.error(t('common.unexpected_code', { code: `LCL-PDVRP-DWL${dateReport}` }));
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onEventSelected = (ev: CatalogEvent)=>{
		if(!dateReport) return true;
		var rp = REPORTS[dateReport];
		if(!rp) return;
		rp.onDownload({
			event_id: ev.event_id,
			date_start: rp.ask_date_range ? dates.data.date_start : null,
			date_end: rp.ask_date_range ? dates.data.date_end : null,
		}).then(url=>{
			if(!url) return;
			setDateReport(null);
			window.open(url, '_blank');
		}).catch(err=>{
			Toast.error(t('common.unexpected_code', { code: `LCL-PDVRP-DWL${dateReport}` }));
		})
		return true;
	}

	return <>
		<Groupper fitted title={t('pdv_reports.title')} style={{ margin: 'auto', maxWidth: 600 }}>
			<div className="ar options">
				{REPORTS.map((a,i)=>(
					<div className="option" key={`RPITM-${i}`}>
						<div className="info">
							<div className="title">{a.title}</div>
							<div className="description">{a.description}</div>
						</div>
						<Button text={a.button_text || t('common.download')} iconName={a.icon} onClick={(setLoading: SetLoading)=>{
							if(a.onClick) return a.onClick(setLoading);
							if(a.ask_date_range){
								return askDate(i);
							}else if(a.ask_event){
								return askEvent(i);
							}
						}} disabled={downloading} />
					</div>
				))}
			</div>
		</Groupper>
		<Modal open={shownModal===Modals.ASK_DATE && dateReport!==null} size='mini' onClose={bindClick(setDateReport)}>
			<Modal.Header>{t('pdv_reports.date_modal.title')}</Modal.Header>
			<Modal.Content>
				<Input label={t('common.date_start')} calendar={{
					mode: 'date',
					date: dates.data.date_start ? fromUnix(dates.data.date_start).toJSDate() : null,
					onSelected: v=>dates.onDataChange('date_start')(v.getTime()/1000),
				}} />
				<Input label={t('common.date_end')} calendar={{
					mode: 'date',
					date: dates.data.date_end ? fromUnix(dates.data.date_end).toJSDate() : null,
					onSelected: v=>dates.onDataChange('date_end')(v.getTime()/1000),
				}} />
				<Message list={dates.prompts} type='error' />
			</Modal.Content>
			<Modal.Actions>
				<Button text={t('common.close')} basic onClick={bindClick(setDateReport)} />
				<Button text={t('common.send')} color='black' onClick={onDateSelected} />
			</Modal.Actions>
		</Modal>
		<SearchEventModal
			open={shownModal===Modals.SELECT_EVENT}
			onClose={bindClick(setShownModal)}
			onEventSelected={onEventSelected}
		/>
	</>
}

export default PdvReports;